import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, ChevronRight, Calendar, MapPin, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import apiService from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface ServiceRequest {
  id: string;
  service_category: string;
  service_category_name: string;
  property_type: string;
  service_type: string;
  service_type_name: string;
  contact_email: string;
  contact_name: string;
  status: string;
  created_at: string;
  quoted_price?: number;
  details?: any;
}

const ServiceRequestsListPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchServiceRequests = async () => {
      try {
        setLoading(true);
        const response = await apiService.request('/customer/service-requests', 'GET');
        
        if (response.success && Array.isArray(response.data)) {
          setServiceRequests(response.data);
        } else {
          setServiceRequests([]);
        }
      } catch (error) {
        console.error('Error fetching service requests:', error);
        toast({ title: 'Error', description: 'Failed to load service requests', variant: 'destructive' });
        setServiceRequests([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchServiceRequests();
    }
  }, [user?.id, toast]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'quoted':
        return <AlertCircle className="h-5 w-5 text-blue-500" />;
      case 'assigned':
        return <CheckCircle className="h-5 w-5 text-purple-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      quoted: { bg: 'bg-blue-100', text: 'text-blue-800' },
      assigned: { bg: 'bg-purple-100', text: 'text-purple-800' },
      completed: { bg: 'bg-green-100', text: 'text-green-800' }
    };

    const badge = badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800' };
    return `${badge.bg} ${badge.text}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Service Requests | Virewire Property Services</title>
        <meta name="description" content="View all your service requests in your Virewire customer dashboard." />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-4xl font-bold text-[#00543b]">Service Requests</h1>
            <Link to="/dashboard/request-service">
              <Button className="bg-[#00543b] hover:bg-[#003d2d]">
                <Plus className="h-4 w-4 mr-2" />
                Request New Service
              </Button>
            </Link>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Service Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12 text-gray-500">
                  <p>Loading...</p>
                </div>
              ) : serviceRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg mb-4">No service requests yet</p>
                  <Link to="/dashboard/request-service">
                    <Button className="bg-[#00543b] hover:bg-[#003d2d]">
                      Request Your First Service
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const totalPages = Math.ceil(serviceRequests.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedRequests = serviceRequests.slice(startIndex, endIndex);

                    return (
                      <>
                        {paginatedRequests.map((request) => (
                          <div
                            key={request.id}
                            className="border border-gray-200 rounded-lg p-4 md:p-6 hover:shadow-md transition"
                          >
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-start gap-3 mb-3">
                                  {getStatusIcon(request.status)}
                                  <div>
                                    <h3 className="font-semibold text-lg text-gray-900">
                                      {request.service_type_name?.replace('-', ' ') || 'Service Request'}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {request.service_category_name} 
                                      {request.property_type && ` • ${request.property_type}`}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm text-gray-500 mt-3">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDate(request.created_at)}
                                  </span>
                                  {request.contact_name && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-4 w-4" />
                                      {request.contact_name}
                                    </span>
                                  )}
                                  {request.contact_email && (
                                    <span className="text-gray-500 text-xs">
                                      {request.contact_email}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${getStatusBadge(request.status)}`}>
                                  {request.status?.toUpperCase()}
                                </span>

                                {request.quoted_price && (
                                  <div className="text-right">
                                    <p className="text-sm text-gray-600">Price</p>
                                    <p className="text-xl font-bold text-green-600">
                                      £{parseFloat(request.quoted_price).toFixed(2)}
                                    </p>
                                  </div>
                                )}

                                <button className="text-accent hover:text-accent/80 transition">
                                  <ChevronRight className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
                            <div className="text-sm text-gray-600">
                              Showing {startIndex + 1} - {Math.min(endIndex, serviceRequests.length)} of {serviceRequests.length} service requests
                            </div>
                            <div className="flex gap-2 items-center">
                              <Button
                                variant="outline"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(currentPage - 1)}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </Button>
                              <div className="flex gap-1">
                                {(() => {
                                  const pages = [];
                                  const maxVisible = 5;
                                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                                  
                                  if (endPage - startPage < maxVisible - 1) {
                                    startPage = Math.max(1, endPage - maxVisible + 1);
                                  }

                                  if (startPage > 1) {
                                    pages.push(
                                      <Button
                                        key={1}
                                        variant={currentPage === 1 ? "default" : "outline"}
                                        onClick={() => setCurrentPage(1)}
                                        className={currentPage === 1 ? "bg-[#00543b] hover:bg-[#003d2d]" : ""}
                                      >
                                        1
                                      </Button>
                                    );
                                    if (startPage > 2) {
                                      pages.push(
                                        <span key="dots-start" className="px-2 text-gray-600">...</span>
                                      );
                                    }
                                  }

                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <Button
                                        key={i}
                                        variant={currentPage === i ? "default" : "outline"}
                                        onClick={() => setCurrentPage(i)}
                                        className={currentPage === i ? "bg-[#00543b] hover:bg-[#003d2d]" : ""}
                                      >
                                        {i}
                                      </Button>
                                    );
                                  }

                                  if (endPage < totalPages) {
                                    if (endPage < totalPages - 1) {
                                      pages.push(
                                        <span key="dots-end" className="px-2 text-gray-600">...</span>
                                      );
                                    }
                                    pages.push(
                                      <Button
                                        key={totalPages}
                                        variant={currentPage === totalPages ? "default" : "outline"}
                                        onClick={() => setCurrentPage(totalPages)}
                                        className={currentPage === totalPages ? "bg-[#00543b] hover:bg-[#003d2d]" : ""}
                                      >
                                        {totalPages}
                                      </Button>
                                    );
                                  }

                                  return pages;
                                })()}
                              </div>
                              <Button
                                variant="outline"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(currentPage + 1)}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  );
};

export default ServiceRequestsListPage;
